import {
  App,
  applicationDefault,
  cert,
  initializeApp,
} from "firebase-admin/app";
import { getMessaging, Message, Messaging } from "firebase-admin/messaging";

import { logger } from "../utils/logger";

/**
 * FCM Service - Firebase Cloud Messaging Service
 * Enterprise-grade push notification service using Firebase Admin SDK
 */
class FCMService {
  private app: App | null = null;
  private initialized = false;

  /**
   * Initialize Firebase Admin SDK
   * Supports multiple initialization methods:
   * 1. Service Account JSON file path
   * 2. Service Account JSON string
   * 3. Application Default Credentials (for GCP)
   */
  initialize(): void {
    try {
      // Skip if already initialized
      if (this.initialized && this.app) {
        logger.info("FCM Service already initialized");
        return;
      }

      const projectId = process.env.FIREBASE_PROJECT_ID;
      const clientEmail = process.env.FIREBASE_CLIENT_EMAIL;
      const privateKey = process.env.FIREBASE_PRIVATE_KEY;
      const serviceAccountPath = process.env.FIREBASE_SERVICE_ACCOUNT_PATH;

      // Method 1: Using service account credentials from environment variables
      if (projectId && clientEmail && privateKey) {
        logger.info("Initializing FCM with environment variables");

        this.app = initializeApp({
          credential: cert({
            projectId,
            clientEmail,
            // Handle escaped newlines in private key
            privateKey: privateKey.replaceAll(String.raw`\n`, "\n"),
          }),
        });

        this.initialized = true;
        logger.info("FCM Service initialized successfully with credentials");
        return;
      }

      // Method 2: Using service account JSON file path
      if (serviceAccountPath) {
        logger.info("Initializing FCM with service account file");

        this.app = initializeApp({
          credential: cert(serviceAccountPath),
        });

        this.initialized = true;
        logger.info(
          "FCM Service initialized successfully with service account file",
        );
        return;
      }

      // Method 3: Application Default Credentials (for GCP environments)
      logger.info("Initializing FCM with Application Default Credentials");

      this.app = initializeApp({
        credential: applicationDefault(),
      });

      this.initialized = true;
      logger.info("FCM Service initialized successfully with ADC");
    } catch (error: any) {
      logger.error("FCM Service initialization failed:", error);
      this.initialized = false;
      // Don't throw error - allow app to run without FCM
      logger.warn("App will continue without push notification support");
    }
  }

  /**
   * Check if FCM is initialized and ready
   */
  isInitialized(): boolean {
    return this.initialized && this.app !== null;
  }

  /**
   * Get Firebase Admin App instance
   */
  getApp(): App | null {
    return this.app;
  }

  /**
   * Get Firebase Messaging instance
   */
  getMessaging(): Messaging | null {
    if (!this.isInitialized() || !this.app) {
      logger.warn("FCM not initialized, cannot get messaging instance");
      return null;
    }

    return getMessaging(this.app);
  }

  /**
   * Send push notification to a single device
   */
  async sendToDevice(
    fcmToken: string,
    notification: {
      title: string;
      body: string;
      imageUrl?: string;
    },
    data?: Record<string, string>,
    options?: {
      priority?: "high" | "normal";
      timeToLive?: number; // in seconds
      collapseKey?: string;
      badge?: number;
      sound?: string;
      channelId?: string;
    },
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        return {
          success: false,
          error: "FCM not initialized",
        };
      }

      // Validate FCM token
      if (!fcmToken || fcmToken.trim() === "") {
        return {
          success: false,
          error: "Invalid FCM token",
        };
      }

      // Build message payload
      const message: Message = {
        token: fcmToken,
        notification: {
          title: notification.title,
          body: notification.body,
          ...(notification.imageUrl && { imageUrl: notification.imageUrl }),
        },
        data: data || {},
        android: {
          priority: options?.priority || "high",
          ttl: options?.timeToLive ? options.timeToLive * 1000 : 3_600_000, // Default 1 hour
          notification: {
            ...(options?.sound && { sound: options.sound }),
            ...(options?.channelId && { channelId: options.channelId }),
            ...(options?.badge !== undefined && {
              notificationCount: options.badge,
            }),
            clickAction: "FLUTTER_NOTIFICATION_CLICK",
          },
          ...(options?.collapseKey && { collapseKey: options.collapseKey }),
        },
        apns: {
          headers: {
            "apns-priority": options?.priority === "high" ? "10" : "5",
          },
          payload: {
            aps: {
              alert: {
                title: notification.title,
                body: notification.body,
              },
              ...(options?.sound && { sound: options.sound }),
              ...(options?.badge !== undefined && { badge: options.badge }),
              contentAvailable: true,
            },
          },
        },
        webpush: {
          notification: {
            title: notification.title,
            body: notification.body,
            ...(notification.imageUrl && { image: notification.imageUrl }),
            ...(options?.badge !== undefined && {
              badge: String(options.badge),
            }),
          },
        },
      };

      // Send message
      const messageId = await messaging.send(message);

      logger.info(`Push notification sent successfully: ${messageId}`);

      return {
        success: true,
        messageId,
      };
    } catch (error: any) {
      logger.error("Error sending push notification:", error);

      // Handle specific FCM errors
      if (error.code === "messaging/invalid-registration-token") {
        return {
          success: false,
          error: "Invalid FCM token",
        };
      }

      if (error.code === "messaging/registration-token-not-registered") {
        return {
          success: false,
          error: "FCM token not registered",
        };
      }

      return {
        success: false,
        error: error.message || "Failed to send push notification",
      };
    }
  }

  /**
   * Send push notification to multiple devices
   */
  async sendToMultipleDevices(
    fcmTokens: string[],
    notification: {
      title: string;
      body: string;
      imageUrl?: string;
    },
    data?: Record<string, string>,
    options?: {
      priority?: "high" | "normal";
      timeToLive?: number;
      collapseKey?: string;
      badge?: number;
      sound?: string;
      channelId?: string;
    },
  ): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
    results: { token: string; success: boolean; error?: string }[];
  }> {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        return {
          success: false,
          successCount: 0,
          failureCount: fcmTokens.length,
          results: fcmTokens.map(token => ({
            token,
            success: false,
            error: "FCM not initialized",
          })),
        };
      }

      // Filter valid tokens
      const validTokens = fcmTokens.filter(
        token => token && token.trim() !== "",
      );

      if (validTokens.length === 0) {
        return {
          success: false,
          successCount: 0,
          failureCount: 0,
          results: [],
        };
      }

      // Build per-device messages (sendEach avoids deprecated multicast APIs)
      const messages: Message[] = validTokens.map(token => ({
        token,
        notification: {
          title: notification.title,
          body: notification.body,
          ...(notification.imageUrl && { imageUrl: notification.imageUrl }),
        },
        data: data || {},
        android: {
          priority: options?.priority || "high",
          ttl: options?.timeToLive ? options.timeToLive * 1000 : 3_600_000,
          notification: {
            ...(options?.sound && { sound: options.sound }),
            ...(options?.channelId && { channelId: options.channelId }),
            ...(options?.badge !== undefined && {
              notificationCount: options.badge,
            }),
            clickAction: "FLUTTER_NOTIFICATION_CLICK",
          },
          ...(options?.collapseKey && { collapseKey: options.collapseKey }),
        },
        apns: {
          headers: {
            "apns-priority": options?.priority === "high" ? "10" : "5",
          },
          payload: {
            aps: {
              alert: {
                title: notification.title,
                body: notification.body,
              },
              ...(options?.sound && { sound: options.sound }),
              ...(options?.badge !== undefined && { badge: options.badge }),
              contentAvailable: true,
            },
          },
        },
      }));

      const response = await messaging.sendEach(messages);

      // Process results
      const results = validTokens.map((token, index) => ({
        token,
        success: response.responses[index].success,
        error: response.responses[index].error?.message,
      }));

      logger.info(
        `Multicast notification sent: ${response.successCount} success, ${response.failureCount} failure`,
      );

      return {
        success: response.successCount > 0,
        successCount: response.successCount,
        failureCount: response.failureCount,
        results,
      };
    } catch (error: any) {
      logger.error("Error sending multicast push notification:", error);

      return {
        success: false,
        successCount: 0,
        failureCount: fcmTokens.length,
        results: fcmTokens.map(token => ({
          token,
          success: false,
          error: error.message || "Failed to send push notification",
        })),
      };
    }
  }

  /**
   * Send push notification to a topic
   */
  async sendToTopic(
    topic: string,
    notification: {
      title: string;
      body: string;
      imageUrl?: string;
    },
    data?: Record<string, string>,
    options?: {
      priority?: "high" | "normal";
      timeToLive?: number;
      collapseKey?: string;
    },
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        return {
          success: false,
          error: "FCM not initialized",
        };
      }

      const message: Message = {
        topic,
        notification: {
          title: notification.title,
          body: notification.body,
          ...(notification.imageUrl && { imageUrl: notification.imageUrl }),
        },
        data: data || {},
        android: {
          priority: options?.priority || "high",
          ttl: options?.timeToLive ? options.timeToLive * 1000 : 3_600_000,
          ...(options?.collapseKey && { collapseKey: options.collapseKey }),
        },
        apns: {
          headers: {
            "apns-priority": options?.priority === "high" ? "10" : "5",
          },
        },
      };

      const messageId = await messaging.send(message);

      logger.info(`Topic notification sent successfully: ${messageId}`);

      return {
        success: true,
        messageId,
      };
    } catch (error: any) {
      logger.error("Error sending topic notification:", error);

      return {
        success: false,
        error: error.message || "Failed to send topic notification",
      };
    }
  }

  /**
   * Subscribe devices to a topic
   */
  async subscribeToTopic(
    fcmTokens: string[],
    topic: string,
  ): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
  }> {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        return {
          success: false,
          successCount: 0,
          failureCount: fcmTokens.length,
        };
      }

      const response = await messaging.subscribeToTopic(fcmTokens, topic);

      logger.info(
        `Subscribed to topic ${topic}: ${response.successCount} success, ${response.failureCount} failure`,
      );

      return {
        success: response.successCount > 0,
        successCount: response.successCount,
        failureCount: response.failureCount,
      };
    } catch (error: any) {
      logger.error("Error subscribing to topic:", error);

      return {
        success: false,
        successCount: 0,
        failureCount: fcmTokens.length,
      };
    }
  }

  /**
   * Unsubscribe devices from a topic
   */
  async unsubscribeFromTopic(
    fcmTokens: string[],
    topic: string,
  ): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
  }> {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        return {
          success: false,
          successCount: 0,
          failureCount: fcmTokens.length,
        };
      }

      const response = await messaging.unsubscribeFromTopic(fcmTokens, topic);

      logger.info(
        `Unsubscribed from topic ${topic}: ${response.successCount} success, ${response.failureCount} failure`,
      );

      return {
        success: response.successCount > 0,
        successCount: response.successCount,
        failureCount: response.failureCount,
      };
    } catch (error: any) {
      logger.error("Error unsubscribing from topic:", error);

      return {
        success: false,
        successCount: 0,
        failureCount: fcmTokens.length,
      };
    }
  }

  /**
   * Validate FCM token
   */
  async validateToken(fcmToken: string): Promise<boolean> {
    try {
      const messaging = this.getMessaging();
      if (!messaging) {
        return false;
      }

      // Try to send a dry-run message to validate token
      await messaging.send(
        {
          token: fcmToken,
          notification: {
            title: "Test",
            body: "Test",
          },
        },
        true, // dry run
      );

      return true;
    } catch (error: any) {
      logger.warn(`Invalid FCM token: ${error.message}`);
      return false;
    }
  }
}

// Export singleton instance
export const fcmService = new FCMService();
